import {
  DOCUMENT_ORDER_RULE_LIMITS,
  SIGNING_ORDER_MAX,
  SIGNING_ORDER_MIN,
  type DocumentOrderRule,
} from '@tes/domain';

/** One editable row; `order` is null while the number field is empty. */
export interface RuleRow {
  id: number;
  keyword: string;
  order: number | null;
}

/** Validate the edited rows the way PATCH /admin/settings will, so a save never 400s. */
export function rulesFromRows(rows: readonly RuleRow[]): { rules: DocumentOrderRule[]; errors: string[] } {
  const errors: string[] = [];
  const rules: DocumentOrderRule[] = [];
  const seen = new Set<string>();
  rows.forEach((row, i) => {
    const keyword = row.keyword.trim();
    const n = i + 1;
    if (!keyword) errors.push(`Rule ${n}: enter a keyword.`);
    else if (keyword.length > DOCUMENT_ORDER_RULE_LIMITS.keywordMaxLength) {
      errors.push(`Rule ${n}: keep the keyword to ${DOCUMENT_ORDER_RULE_LIMITS.keywordMaxLength} characters.`);
    } else if (seen.has(keyword.toLowerCase())) {
      errors.push(`Rule ${n}: "${keyword}" is already listed (upper/lower case doesn't matter).`);
    }
    seen.add(keyword.toLowerCase());
    const { order } = row;
    if (order === null || !Number.isInteger(order) || order < SIGNING_ORDER_MIN || order > SIGNING_ORDER_MAX) {
      errors.push(`Rule ${n}: position must be a whole number from ${SIGNING_ORDER_MIN} to ${SIGNING_ORDER_MAX}.`);
    } else if (keyword) {
      rules.push({ keyword, order });
    }
  });
  if (rows.length > DOCUMENT_ORDER_RULE_LIMITS.maxRules) {
    errors.push(`Use at most ${DOCUMENT_ORDER_RULE_LIMITS.maxRules} rules.`);
  }
  return { rules, errors };
}

/** Whether the rows differ from the saved rules (keywords compared trimmed, as they'd be saved). */
export function rowsChanged(saved: readonly DocumentOrderRule[], rows: readonly RuleRow[]): boolean {
  return (
    saved.length !== rows.length ||
    rows.some((r, i) => r.keyword.trim() !== saved[i].keyword || r.order !== saved[i].order)
  );
}
